/**
 * EnrollmentsList - List of campaign enrollments
 */
import { Building2, ChevronRight, Clock, CheckCircle, Pause, XCircle } from 'lucide-react';
import {
  Card,
  CardContent,
  Button,
  cn,
} from '@respectly/ui';
import type { CampaignEnrollment, EnrollmentStatus, CampaignStep } from '@respectly/shared-types';

// ============================================
// Config
// ============================================

const statusConfig: Record<EnrollmentStatus, { label: string; icon: typeof CheckCircle; dotColor: string; textColor: string; bgColor: string }> = {
  active: {
    label: 'Active',
    icon: Clock,
    dotColor: 'bg-blue-500',
    textColor: 'text-blue-700 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-900/20',
  },
  completed: {
    label: 'Completed',
    icon: CheckCircle,
    dotColor: 'bg-emerald-500',
    textColor: 'text-emerald-700 dark:text-emerald-400',
    bgColor: 'bg-emerald-50 dark:bg-emerald-900/20',
  },
  paused: {
    label: 'Paused',
    icon: Pause,
    dotColor: 'bg-amber-500',
    textColor: 'text-amber-700 dark:text-amber-400',
    bgColor: 'bg-amber-50 dark:bg-amber-900/20',
  },
  exited: {
    label: 'Exited',
    icon: XCircle,
    dotColor: 'bg-red-500',
    textColor: 'text-red-700 dark:text-red-400',
    bgColor: 'bg-red-50 dark:bg-red-900/20',
  },
};

// ============================================
// Types
// ============================================

interface EnrollmentsListProps {
  enrollments: CampaignEnrollment[];
  steps: CampaignStep[];
  isLoading?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  className?: string;
}

// ============================================
// Component
// ============================================

export function EnrollmentsList({
  enrollments,
  steps,
  isLoading,
  onLoadMore,
  hasMore,
  className,
}: EnrollmentsListProps) {
  if (isLoading && enrollments.length === 0) {
    return (
      <div className={cn('space-y-2', className)}>
        {Array.from({ length: 5 }).map((_, i) => (
          <EnrollmentItemSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (enrollments.length === 0) {
    return (
      <div className={cn('text-center py-12', className)}>
        <Building2 className="h-10 w-10 text-slate-200 dark:text-slate-700 mx-auto mb-3" />
        <h3 className="font-medium text-slate-700 dark:text-slate-300 mb-1 text-sm">
          No enrollments yet
        </h3>
        <p className="text-xs text-slate-500 dark:text-slate-400 max-w-xs mx-auto">
          Accounts will appear here once they are enrolled in this campaign.
        </p>
      </div>
    );
  }

  return (
    <div className={cn('space-y-2', className)}>
      {enrollments.map((enrollment) => (
        <EnrollmentItem key={enrollment.id} enrollment={enrollment} steps={steps} />
      ))}
      {hasMore && onLoadMore && (
        <Button variant="outline" className="w-full" onClick={onLoadMore} disabled={isLoading}>
          {isLoading ? 'Loading...' : 'Load More'}
        </Button>
      )}
    </div>
  );
}

// ============================================
// Enrollment Item
// ============================================

interface EnrollmentItemProps {
  enrollment: CampaignEnrollment;
  steps: CampaignStep[];
}

function EnrollmentItem({ enrollment, steps }: EnrollmentItemProps) {
  const config = statusConfig[enrollment.status];
  const StatusIcon = config.icon;
  const totalSteps = steps.length;
  const progress =
    totalSteps > 0
      ? Math.round(((enrollment.currentStepIndex) / totalSteps) * 100)
      : 0;

  return (
    <Card className="hover:shadow-sm transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          {/* Icon */}
          <div className="h-8 w-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
            <Building2 className="h-4 w-4 text-slate-400" />
          </div>

          {/* Tenant info */}
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm text-slate-900 dark:text-white truncate">
              {enrollment.tenant?.name || 'Unknown Account'}
            </div>
            <div className="text-xs text-slate-400 mt-0.5">
              Enrolled {new Date(enrollment.startedAt).toLocaleDateString()}
            </div>
          </div>

          {/* Step progress */}
          <div className="flex-shrink-0 text-center hidden sm:block">
            <div className="text-xs text-slate-500 mb-1 tabular-nums">
              Step {Math.min(enrollment.currentStepIndex + 1, totalSteps)} / {totalSteps}
            </div>
            <div className="w-20 h-1 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-400 rounded-full transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Status badge */}
          <div className={cn(
            'flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium flex-shrink-0',
            config.bgColor,
            config.textColor
          )}>
            <StatusIcon className="h-3 w-3" />
            {config.label}
          </div>

          <ChevronRight className="h-4 w-4 text-slate-300 dark:text-slate-600 flex-shrink-0" />
        </div>

        {/* Exit reason */}
        {enrollment.status === 'exited' && enrollment.exitedAt && (
          <div className="mt-2.5 pt-2.5 border-t border-slate-100 dark:border-slate-800">
            <div className="flex items-start gap-1.5 text-xs text-red-500 dark:text-red-400">
              <XCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              <span>
                Exited {new Date(enrollment.exitedAt).toLocaleDateString()}
                {enrollment.exitReason && (
                  <span className="text-slate-500 dark:text-slate-400"> — {enrollment.exitReason}</span>
                )}
              </span>
            </div>
          </div>
        )}

        {/* Completed date */}
        {enrollment.status === 'completed' && enrollment.completedAt && (
          <div className="mt-2.5 pt-2.5 border-t border-slate-100 dark:border-slate-800">
            <div className="flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400">
              <CheckCircle className="h-3.5 w-3.5" />
              <span>Completed {new Date(enrollment.completedAt).toLocaleDateString()}</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================
// Skeleton
// ============================================

function EnrollmentItemSkeleton() {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          <div className="h-8 w-8 rounded-lg bg-slate-200 dark:bg-slate-700 animate-pulse flex-shrink-0" />
          <div className="flex-1">
            <div className="h-4 w-32 bg-slate-200 dark:bg-slate-700 rounded animate-pulse mb-1" />
            <div className="h-3 w-24 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
          </div>
          <div className="h-5 w-16 bg-slate-200 dark:bg-slate-700 rounded-full animate-pulse" />
        </div>
      </CardContent>
    </Card>
  );
}
